import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { AuthService } from '../../services/auth.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { UdRegisterService } from '../../services/udRegister.service';
import { BuyerOrderService } from '../../services/buyerOrder.service';
import { ItemService } from '../../services/item.service';
import { UdReconciliationService } from '../../services/garmentsCompliance.service';
import { IUdReconciliation, IUdReconciliationLine } from '../../interfaces/garmentsCompliance.interface';
import { IUdRegister } from '../../interfaces/commercial.interface';
import { IBuyerOrder } from '../../interfaces/garments/buyerOrder.interface';
import { Item } from '../../interfaces/item.interface';
import { PERMISSIONS } from '../../constants/permissions';
import { triggerDownload } from '../statutoryRegisters/shared';
import SearchableSelect from '../common/SearchableSelect';
import { humanizeEnum } from '../../utils/format';

const FLAG_LABEL: Record<string, string> = {
    IMPORT_EXCEEDS_ENTITLEMENT: 'Imported above entitlement',
    UTILIZATION_MISMATCH: 'Recorded utilisation differs from imports',
    CONSUMED_EXCEEDS_ALLOWED: 'Consumed above allowed (BOM + wastage)',
    UNMAPPED: 'Map an item to reconcile',
};

const qty = (n?: number | null) => (n === null || n === undefined ? '—' : n.toLocaleString('en-US', { maximumFractionDigits: 3 }));

/**
 * UD utilisation reconciliation (W3-W): per entitlement line, entitled vs imported vs recorded utilisation vs
 * consumed vs consumption in exported goods, with flags and a BGMEA-style statement download (XLSX and PDF).
 */
const UdReconciliation: React.FC = () => {
    const toast = useToast();
    const { hasPermission } = useAuth();
    const institutionId = AuthService.getSelectedInstitution()!;
    const canManage = hasPermission(PERMISSIONS.UD_MANAGE);
    const [uds, setUds] = useState<IUdRegister[]>([]);
    const [orders, setOrders] = useState<IBuyerOrder[]>([]);
    const [items, setItems] = useState<Item[]>([]);
    const [udId, setUdId] = useState('');
    const [tolerance, setTolerance] = useState('1');
    const [data, setData] = useState<IUdReconciliation | null>(null);
    const [loading, setLoading] = useState(false);
    const [newOrderId, setNewOrderId] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const [u, o, i] = await Promise.all([
                    UdRegisterService.list(institutionId, 0, 200),
                    BuyerOrderService.list(institutionId, 0, 200),
                    ItemService.getItemsByInstitution(0, 200),
                ]);
                setUds(u.content);
                setOrders(o.content);
                setItems(i.content);
            } catch (e) {
                toast.showApiError(e, 'Failed to load the UD, order and item lists');
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const tol = (): number | undefined => (tolerance.trim() === '' || Number.isNaN(Number(tolerance)) ? undefined : Number(tolerance));

    const load = useCallback(async (id: string) => {
        if (!id) { setData(null); return; }
        setLoading(true);
        try {
            setData(await UdReconciliationService.get(institutionId, id, tol()));
        } catch (e) {
            setData(null);
            toast.showApiError(e, 'Failed to load the UD reconciliation');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId, tolerance]);

    const run = async (action: () => Promise<unknown>, failure: string) => {
        try {
            const result = await action() as { warning?: string | null } | undefined;
            if (result?.warning) toast.showWarning(result.warning);
            await load(udId);
        } catch (e) {
            toast.showApiError(e, failure);
        }
    };

    const download = async (kind: 'xlsx' | 'pdf') => {
        if (!data) return;
        try {
            const blob = kind === 'xlsx'
                ? await UdReconciliationService.downloadXlsx(institutionId, udId, tol())
                : await UdReconciliationService.downloadPdf(institutionId, udId, tol());
            triggerDownload(blob, `ud-statement-${data.udNumber}.${kind}`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        }
    };

    const linkedIds = new Set((data?.orders ?? []).map(o => o.buyerOrderId));

    return (
        <Box data-help-page="commercial.udRecon.page">
            <Typography variant="h5" sx={{ mb: 2 }}>UD Reconciliation</Typography>
            <Box data-help-section="commercial.udRecon.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ flex: '1 1 320px', maxWidth: 460 }}>
                    <SearchableSelect<IUdRegister>
                        data-help="commercial.udRecon.ud" label="UD" size="small" options={uds} value={udId} data-testid="ud-select"
                        getOptionLabel={u => `${u.udNumber} (${u.udDate})`}
                        onChange={id => { setUdId(id); load(id); }}
                    />
                </Box>
                <Box sx={{ flex: '1 1 160px', maxWidth: 220 }}>
                    <TextField data-help="commercial.udRecon.tolerance" label="Tolerance %" size="small" value={tolerance} onChange={e => setTolerance(e.target.value)}
                        helperText="Recorded vs imported (verify)" fullWidth />
                </Box>
                <Button data-help="commercial.udRecon.refresh" variant="outlined" size="small" onClick={() => load(udId)} disabled={!udId || loading}>Refresh</Button>
                <Button data-help="commercial.udRecon.xlsx" variant="contained" size="small" startIcon={<FileDownloadIcon />} disabled={!data} onClick={() => download('xlsx')}>XLSX</Button>
                <Button data-help="commercial.udRecon.pdf" variant="contained" size="small" startIcon={<PictureAsPdfIcon />} disabled={!data} onClick={() => download('pdf')}>PDF</Button>
            </Box>

            {loading && <CircularProgress size={24} />}
            {!udId && !loading && <Typography color="text.secondary">Select a UD to reconcile its entitlement against imports, consumption and exports.</Typography>}

            {data && !loading && (
                <>
                    <Alert severity="info" sx={{ mb: 2 }}>{data.verifyNote}</Alert>
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle1">UD {data.udNumber} dated {data.udDate} ({humanizeEnum(data.status)})</Typography>
                        <Typography variant="body2" color="text.secondary">
                            LC {data.lcNumber ?? '—'} · Buyer {data.buyerName || '—'} · Issued by {data.issuingAuthority} · {data.flaggedLines} flagged line(s)
                        </Typography>
                    </Paper>
                    {data.warnings.map(w => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}

                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Orders covered by this UD</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                            {data.orders.length === 0 && <Typography variant="body2" color="text.secondary">None linked.</Typography>}
                            {data.orders.map(o => (
                                <Chip key={o.id} label={`${o.orderNumber ?? 'Order'}${o.buyerName ? ' · ' + o.buyerName : ''}${o.live ? '' : ' (not live)'}`}
                                    color={o.live ? 'default' : 'warning'}
                                    onDelete={canManage ? () => run(() => UdReconciliationService.unlinkOrder(institutionId, udId, o.id), 'Failed to unlink the order') : undefined} />
                            ))}
                        </Box>
                        {canManage && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                <Box sx={{ flex: '1 1 260px', maxWidth: 380 }}>
                                    <SearchableSelect<IBuyerOrder>
                                        data-help="commercial.udRecon.linkOrder" label="Link a buyer order" size="small" value={newOrderId}
                                        options={orders.filter(o => !linkedIds.has(o.id))}
                                        getOptionLabel={o => o.orderNumber} onChange={id => setNewOrderId(id)} />
                                </Box>
                                <Button data-help="commercial.udRecon.linkBtn" size="small" variant="outlined" disabled={!newOrderId}
                                    onClick={() => run(async () => { await UdReconciliationService.linkOrder(institutionId, udId, newOrderId); setNewOrderId(''); }, 'Failed to link the order')}>
                                    Link
                                </Button>
                            </Box>
                        )}
                    </Paper>

                    <TableContainer component={Paper} data-testid="ud-recon-table" data-help-section="commercial.udRecon.table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="commercial.udRecon.col.item">Item</TableCell>
                                    <TableCell data-help="commercial.udRecon.col.hs">HS code</TableCell>
                                    <TableCell align="right" data-help="commercial.udRecon.col.entitled">Entitled</TableCell>
                                    <TableCell align="right" data-help="commercial.udRecon.col.imported">Imported</TableCell>
                                    <TableCell align="right" data-help="commercial.udRecon.col.recorded">Recorded util.</TableCell>
                                    <TableCell align="right" data-help="commercial.udRecon.col.consumed">Consumed</TableCell>
                                    <TableCell align="right" data-help="commercial.udRecon.col.allowed">Allowed</TableCell>
                                    <TableCell align="right" data-help="commercial.udRecon.col.inExports">In exports</TableCell>
                                    <TableCell align="right" data-help="commercial.udRecon.col.balance">Balance in bond</TableCell>
                                    <TableCell data-help="commercial.udRecon.col.flags">Flags</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.lines.map((l: IUdReconciliationLine) => (
                                    <TableRow key={l.lineId} hover data-testid={`ud-recon-row-${l.lineId}`}
                                        sx={l.flags.length > 0 ? { backgroundColor: 'action.hover' } : undefined}>
                                        <TableCell sx={{ minWidth: 220 }}>
                                            {canManage ? (
                                                <SearchableSelect<Item>
                                                    data-help="commercial.udRecon.mapItem" size="small" label={l.description ?? 'Entitlement line'} value={l.itemId ?? ''}
                                                    options={items} getOptionLabel={i => `${i.itemCode} — ${i.itemName}`}
                                                    onChange={id => run(() => UdReconciliationService.mapLineItem(institutionId, udId, l.lineId, id || null), 'Failed to map the item')} />
                                            ) : (l.itemCode ? `${l.itemCode} — ${l.itemName ?? ''}` : (l.description ?? '—'))}
                                        </TableCell>
                                        <TableCell>{l.hsCode ?? '—'}</TableCell>
                                        <TableCell align="right">{qty(l.entitledQty)}</TableCell>
                                        <TableCell align="right">{qty(l.importedQty)}</TableCell>
                                        <TableCell align="right">{qty(l.recordedUtilizationQty)}</TableCell>
                                        <TableCell align="right">{qty(l.consumedQty)}</TableCell>
                                        <TableCell align="right">{qty(l.allowedQty)}</TableCell>
                                        <TableCell align="right">{qty(l.exportConsumptionQty)}</TableCell>
                                        <TableCell align="right">{qty(l.balanceInBondQty)}</TableCell>
                                        <TableCell>
                                            {l.flags.length === 0 ? '—' : l.flags.map(f => (
                                                <Tooltip key={f} title={FLAG_LABEL[f] ?? f}>
                                                    <Chip size="small" color={f === 'UNMAPPED' ? 'default' : 'error'} label={FLAG_LABEL[f] ?? f} sx={{ mr: 0.5, mb: 0.5 }} />
                                                </Tooltip>
                                            ))}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {data.lines.length === 0 && (
                                    <TableRow><TableCell colSpan={10} align="center"><Typography color="text.secondary">This UD has no entitlement lines.</Typography></TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    );
};

export default UdReconciliation;
